import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Play, Square, Download, CheckCircle, XCircle, AlertTriangle, ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import { StorageImage } from './StorageImage';
import { ImageZoomOverlay } from './ImageZoomOverlay';
import { runWithConcurrency } from './utils/concurrencyPool';
import { supportsSamplingParams } from './utils/geminiSampling';
import { measureImagesSharpness, isBlurryImage, blurryImageBadgeHtml, BLURRY_IMAGE_LABEL } from './utils/imageSharpness';
import { exportCSV, formatSerial, isIOS as isIOSDevice } from './utils/format';
import { buildRunSummary, buildRunItems } from './utils/regressionHistory';
import { openBlankTab, openTabWithContent } from './utils/blankTab';
import { AUTO_PRINT_SCRIPT } from './utils/printAutoScript';
import { usePendingPdfShare } from './utils/pdfShare';
import { downscaleForPdf } from './utils/pdfImage';
import RegressionHistory from './RegressionHistory';
import pkg from '../package.json';

const DEFAULT_CONCURRENCY = 8;
const STAGGER_MS = 100;
const TIMEOUT_MS = 90000;
const DOWNLOAD_CONCURRENCY = 6;

async function blobToBase64(blob) {
  return new Promise(resolve => { const r = new FileReader(); r.onloadend = () => resolve(r.result); r.readAsDataURL(blob); });
}

function buildTestItemHtml(r, imgHtml = '') {
  const esc = (s) => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const label = r.ref?.label || '—';
  const expected = r.expected || '—';
  const actual = r.run?.match_status || r.actual || '—';
  const match = r.match;
  const reason = r.run?.reason || '';
  const visual = r.run?.detected_food_visual || '';
  const labelProduct = r.run?.label_product_name || r.ref?.inspections?.label_product_name || '';
  const color = actual === 'SAFE' ? '#2563eb' : actual === 'DANGER' ? '#dc2626' : '#6b7280';
  return `<div style="page-break-inside:avoid;border:1px solid #e2e8f0;border-radius:8px;padding:12px;margin-bottom:8px;background:#fff">
<div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
<span style="font-size:12px;font-weight:bold">${esc(label)}</span>
<span style="font-size:14px;font-weight:900;color:${color}">${actual === 'SAFE' ? '✓ SAFE' : '✗ DANGER'}</span>${blurryImageBadgeHtml(r.run?.image_quality)}
${match ? '' : `<span style="color:#dc2626;font-size:11px;margin-left:8px">期待:${esc(expected)}</span>`}
</div>
<div style="display:flex;gap:12px">
${imgHtml ? `<div style="flex:0 0 180px">${imgHtml}</div>` : ''}
<div style="flex:1;min-width:0">
${labelProduct ? `<div style="font-size:10px;color:#475569;margin-bottom:4px">商品名: ${esc(labelProduct)}</div>` : ''}
${reason ? `<div style="font-size:10px;color:#475569;margin-bottom:4px">理由: ${esc(reason)}</div>` : ''}
${visual ? `<div style="font-size:10px;color:#475569">目視: ${esc(visual)}</div>` : ''}
${r.run?.analyzeMs != null ? `<div style="font-size:10px;color:#94a3b8;margin-top:4px">⏱ ${(r.run.analyzeMs / 1000).toFixed(2)}s</div>` : ''}
</div>
</div>
</div>`;
}

async function downloadImagesForPdf(imageUrls) {
  if (!imageUrls || imageUrls.length === 0) return '';
  const imgs = [];
  for (const path of imageUrls) {
    const { data } = await supabase.storage.from('pm-inspection-images').download(path);
    if (data) {
      // 撮影画像は長辺2048pxまであるが、PDF上は最大180px幅でしか描画しない。そのまま
      // 埋め込むと、特にiOSのhtml2pdf.js（scale 2）で全件PDFのキャンバスが巨大になり、
      // 白紙になったり生成が止まったりする。表示幅の2倍（360px）に縮小して埋め込む
      const b64 = await downscaleForPdf(await blobToBase64(data), 360);
      imgs.push(`<img src="${b64}" style="width:100%;max-width:180px;object-fit:contain;border-radius:6px;border:1px solid #e2e8f0;margin-right:4px" />`);
    }
  }
  return imgs.join('');
}

// iOSでは別タブ（window.open）を開かず、html2pdf.jsでPDFにして共有シートで保存する
// （onIOSPdfReady に usePendingPdfShare の shareOrDefer を渡す）。検査履歴のPDF
// （AdminPanel.js・SuperAdminPanel.js の openInspectionPdf）と同じ方式。
// 従来このPDFはiOS対応が無く、ホーム画面から起動したアプリでは別タブの印刷画面を
// キャンセルした後、その画面から検査画面に戻る手段が無くなっていた（iPhone SEで実際に発生）
// html2pdf.js は文字列で渡したHTMLの <style> 要素を落とす（App.js の照合レポートの
// コメント参照）。本文の書体・文字サイズ・色は body の <style> で指定しているため、
// body の中身を同じ指定のインラインstyle付き<div>で包み直して渡す。<script> は除く
// （ページ余白は html2pdf.js の margin で付ける）
function inlineBodyStyle(html) {
  const body = (html.match(/<body[^>]*>([\s\S]*)<\/body>/) || [null, html])[1]
    .replace(/<script>[\s\S]*?<\/script>/g, '');
  return `<div style="font-family:'Hiragino Sans','Meiryo',sans-serif;font-size:11px;color:#1e293b">${body}</div>`;
}

async function deliverIOSPdf(html, filename, onIOSPdfReady) {
  const html2pdf = (await import('html2pdf.js')).default;
  const worker = html2pdf().set({
    margin: 10,
    image: { type: 'jpeg', quality: 0.95 },
    html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
  }).from(inlineBodyStyle(html), 'string');
  const blob = await worker.outputPdf('blob');
  await onIOSPdfReady(blob, filename);
}

async function openTestPdf(r, exporterEmail = '―', { onIOSPdfReady } = {}) {
  // 画像ダウンロード（下のawait）より前にタブを開いておく（AdminPanel.jsの
  // openBlankTab参照。window.open()を非同期処理の後に呼ぶとブラウザに
  // ポップアップとしてブロックされうる）。iOSはタブを開かない（deliverIOSPdf参照）
  const isIOS = isIOSDevice() && !!onIOSPdfReady;
  const w = isIOS ? null : openBlankTab();
  if (!isIOS && !w) return;
  const esc = (s) => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const dateStr = `${now.getFullYear()}/${pad(now.getMonth()+1)}/${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const imgHtml = await downloadImagesForPdf(r.ref?.inspections?.image_urls);
  const html = `<!DOCTYPE html><html lang="ja"><head><meta charset="UTF-8"><title>PM_テスト結果_${esc(r.ref?.label || '')}</title>
<style>body{font-family:'Hiragino Sans','Meiryo',sans-serif;padding:20px;font-size:11px;color:#1e293b;background:#f8fafc}@media print{@page{margin:10mm}body{background:#fff}html{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style>
</head><body>
<h2 style="font-size:16px;margin:0 0 4px;color:#4f46e5">ProductMatcher テスト結果</h2>
<div style="font-size:11px;color:#64748b;margin-bottom:10px">出力者: ${esc(exporterEmail)} / 出力: ${dateStr}</div>
${buildTestItemHtml(r, imgHtml)}
${AUTO_PRINT_SCRIPT}</body></html>`;
  if (isIOS) {
    await deliverIOSPdf(html, `PM_テスト結果_${r.ref?.label || ''}`, onIOSPdfReady);
    return;
  }
  openTabWithContent(w, html);
}

async function openBatchPdf(results, modelName, exporterEmail = '―', { onIOSPdfReady } = {}) {
  const validResults = results.filter(r => !r.error);
  if (validResults.length === 0) { alert('出力可能な結果がありません'); return; }
  // 画像ダウンロード（下のawait、件数が多いと数秒〜十数秒かかりうる）より前に
  // タブを開いておく（openTestPdf・AdminPanel.jsのopenBlankTab参照）。iOSはタブを開かない
  const isIOS = isIOSDevice() && !!onIOSPdfReady;
  const w = isIOS ? null : openBlankTab();
  if (!isIOS && !w) return;
  const esc = (s) => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const passCount = validResults.filter(r => r.match).length;
  const failCount = validResults.filter(r => !r.match).length;
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const dateStr = `${now.getFullYear()}/${pad(now.getMonth()+1)}/${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const fileDate = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const filename = `PM_テスト結果_${fileDate}`;
  const itemsHtmlParts = await Promise.all(validResults.map(async r => {
    const imgHtml = await downloadImagesForPdf(r.ref?.inspections?.image_urls);
    return buildTestItemHtml(r, imgHtml);
  }));
  const html = `<!DOCTYPE html><html lang="ja"><head><meta charset="UTF-8"><title>${esc(filename)}</title>
<style>body{font-family:'Hiragino Sans','Meiryo',sans-serif;padding:20px;font-size:11px;color:#1e293b;background:#f8fafc}@media print{@page{margin:10mm}body{background:#fff}html{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style>
</head><body>
<h2 style="font-size:16px;margin:0 0 4px;color:#4f46e5">ProductMatcher リグレッションテスト結果</h2>
<div style="font-size:11px;color:#64748b;margin-bottom:10px">出力者: ${esc(exporterEmail)} / 出力: ${dateStr} / モデル: ${esc(modelName || '')}</div>
<div style="margin-bottom:10px;font-size:11px">合計: <b>${validResults.length}件</b>　<span style="color:#2563eb">PASS: <b>${passCount}件</b></span>　<span style="color:#dc2626">FAIL: <b>${failCount}件</b></span></div>
${itemsHtmlParts.join('')}
${AUTO_PRINT_SCRIPT}</body></html>`;
  if (isIOS) {
    await deliverIOSPdf(html, filename, onIOSPdfReady);
    return;
  }
  openTabWithContent(w, html);
}

function calculateStability(runs) {
  if (!runs.length) return { score: 100, consistent: true, counts: {}, majority: null, total: 0 };
  const statuses = runs.map(r => r.match_status);
  const counts = {};
  statuses.forEach(s => { counts[s] = (counts[s] || 0) + 1; });
  const maxCount = Math.max(...Object.values(counts));
  const majority = Object.entries(counts).find(([, c]) => c === maxCount)?.[0];
  return { score: Math.round((maxCount / statuses.length) * 100), consistent: maxCount === statuses.length, majority, counts, total: statuses.length };
}

// AI生成パラメータ（実験用）の選択肢。単一実行のプルダウンと網羅テストのチェックボックスで共用
const THINKING_LEVEL_OPTIONS = [
  { value: 'default', label: '現状（モデル既定）' },
  { value: 'minimal', label: 'minimal' },
  { value: 'low', label: 'low' },
  { value: 'medium', label: 'medium' },
  { value: 'high', label: 'high' },
];
const TEMPERATURE_OPTIONS = [
  // 「現状」は flash-lite 等では temperature 0＋seed、3.6 Flash 以降は seed だけ（temperature は送らない）
  { value: 'default', label: '現状（0＋seed固定。3.6 Flash以降はseedのみ）' },
  { value: 'geminiDefault', label: '未指定（Gemini既定1.0）' },
  { value: '0.3', label: '0.3' },
  { value: '0.7', label: '0.7' },
  { value: '1', label: '1.0（明示）' },
];
// 数値の temperature は、3.6 Flash より前のモデルにだけ送れる（3.6 Flash からは効かず、これからのモデルでは
// Gemini が 400 を返す。Google の通知、2026-10-07。api/analyze.js でも止める）。
// 「現状」「未指定」はどのモデルでもよい（「現状」は 3.6 Flash 以降では seed 固定だけになる）
const isTemperatureModeSupported = (model, mode) =>
  mode === 'default' || mode === 'geminiDefault' || supportsSamplingParams(model);

function buildAnalyzeBody(images, { model, thinkingLevel, temperatureMode, mediaResolution, imageSharpness }) {
  return {
    base64Images: images,
    ...(imageSharpness ? { imageSharpness } : {}),
    modelName: model,
    ...(thinkingLevel && thinkingLevel !== 'default' ? { thinkingLevel } : {}),
    ...(temperatureMode && temperatureMode !== 'default' ? { temperature: temperatureMode === 'geminiDefault' ? 'default' : parseFloat(temperatureMode) } : {}),
    ...(mediaResolution && mediaResolution !== 'default' ? { mediaResolution } : {}),
  };
}

// 戻り値は基準ラベルごとの { images, imageSharpness }。ピントの鋭さ（「画像不鮮明」の判断に使う）は
// 繰り返し解析しても変わらないので、画像の取得時に1回だけ計測する
async function downloadImages(targets, { concurrency, isAborted }) {
  const imagesByRef = new Map();
  await runWithConcurrency(targets, async (ref) => {
    const images = [];
    for (const path of ref.inspections.image_urls) {
      const { data } = await supabase.storage.from('pm-inspection-images').download(path);
      if (data) images.push(await blobToBase64(data));
    }
    imagesByRef.set(ref.id, { images, imageSharpness: await measureImagesSharpness(images) });
  }, { concurrency, isAborted });
  return imagesByRef;
}

// 1パターン分（モデル・思考レベル・temperature・画像解像度の組み合わせ）を実行する。
// 画像は呼び出し側で1回だけダウンロードして使い回す想定。
async function runAnalyzeBatch(targets, imagesByRef, {
  token, model, thinkingLevel, temperatureMode, mediaResolution,
  iterations, mode, concurrency, isAborted, onProgress,
}) {
  const tasks = []; const refRuns = new Map();
  for (const ref of targets) {
    const { images = [], imageSharpness = null } = imagesByRef.get(ref.id) || {};
    if (images.length === 0) continue;
    refRuns.set(ref.id, new Array(iterations).fill(null));
    for (let i = 0; i < iterations; i++) tasks.push({ ref, iteration: i, images, imageSharpness });
  }
  onProgress?.({ current: 0, total: tasks.length });

  await runWithConcurrency(tasks, async ({ ref, iteration, images, imageSharpness }) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const tReq = performance.now();
      const resp = await fetch('/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(buildAnalyzeBody(images, { model, thinkingLevel, temperatureMode, mediaResolution, imageSharpness })),
        signal: controller.signal,
      });
      clearTimeout(timer);
      const analyzeMs = Math.round(performance.now() - tReq);
      if (!resp.ok) { let e = `HTTP ${resp.status}`; try { e = (await resp.json()).error || e; } catch {} return { error: e }; }
      const data = await resp.json();
      return {
        match_status: data.match_status, reason: data.reason, detected_food_visual: data.detected_food_visual,
        label_product_name: data.label_product_name, quantity_status: data.quantity_status, label_consistency: data.label_consistency,
        label_quantity: data.label_quantity, label_quantity_number: data.label_quantity_number, detected_quantity: data.detected_quantity,
        image_quality: data.image_quality ?? null,
        analyzeMs,
      };
    } catch (err) { clearTimeout(timer); return { error: err.name === 'AbortError' ? 'タイムアウト' : err.message }; }
  }, {
    concurrency, staggerMs: STAGGER_MS, isAborted,
    onSettle: ({ item, value, completed, total }) => { onProgress?.({ current: completed, total }); refRuns.get(item.ref.id)[item.iteration] = value; },
  });

  const allResults = [];
  for (const ref of targets) {
    if (!refRuns.has(ref.id)) { allResults.push({ ref, error: '画像DL失敗', runs: [] }); continue; }
    const runs = refRuns.get(ref.id).map(r => r || { error: '中止' });
    if (mode === 'regression') {
      const run = runs[0];
      if (run?.error) { allResults.push({ ref, error: run.error, runs }); }
      else {
        const expected = ref.expected_match_status || ref.inspections?.match_status;
        allResults.push({ ref, expected, actual: run.match_status, match: expected === run.match_status, run, runs });
      }
    } else {
      const validRuns = runs.filter(r => !r.error);
      if (validRuns.length === 0) { allResults.push({ ref, error: '全回エラー', runs }); }
      else { allResults.push({ ref, stability: calculateStability(validRuns), validRuns: validRuns.length, runs }); }
    }
  }
  return allResults;
}

// 履歴をDBに保存し、保存したサマリを返す（失敗しても呼び出し側の結果表示は止めない前提でtry/catchは呼び出し側が行う）
async function saveRunHistory(allResults, { mode, model, appVersion, imageMode, thinkingLevel, temperatureMode, mediaResolution, duration, companyId, createdBy }) {
  const summary = buildRunSummary(allResults, {
    mode, model, appVersion, inspectionMode: imageMode, duration,
    thinkingLevel, temperatureMode, mediaResolution,
  });
  const { data: runData } = await supabase.from('pm_regression_runs').insert({
    company_id: companyId, ...summary, created_by: createdBy,
  }).select('id').single();
  if (runData?.id) {
    const items = buildRunItems(allResults).map(i => ({ ...i, run_id: runData.id }));
    for (let i = 0; i < items.length; i += 100) {
      await supabase.from('pm_regression_run_items').insert(items.slice(i, i + 100));
    }
  }
  return summary;
}

export default function RegressionTestTab({ companies }) {
  const { profile } = useAuth();
  // iOSのPDF保存（deliverIOSPdf参照）。生成が一時的な許可（約5秒）に間に合わなかった
  // 場合や共有に失敗した場合は、生成済みPDFを残して「PDFを保存」ボタンから保存し直す
  const { pendingPdf, shareOrDefer, sharePendingPdf } = usePendingPdfShare();
  const [pdfBusy, setPdfBusy] = useState(false);
  const runPdf = async (fn) => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try { await fn(); }
    catch (err) { console.error('[RegressionTestTab PDF]', err); alert('PDFの生成に失敗しました。件数を減らして再度お試しください。'); }
    finally { setPdfBusy(false); }
  };
  const isSuperAdmin = profile?.role === 'superadmin';
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCompany, setFilterCompany] = useState(() => (isSuperAdmin ? '' : (profile?.company_id || '')));

  const [selectedIds, setSelectedIds] = useState(() => {
    try { const s = localStorage.getItem('pm_regressionSelectedIds'); return s ? new Set(JSON.parse(s)) : new Set(); }
    catch { return new Set(); }
  });
  useEffect(() => { localStorage.setItem('pm_regressionSelectedIds', JSON.stringify([...selectedIds])); }, [selectedIds]);

  const [mode, setMode] = useState('regression');
  const changeMode = (m) => { setMode(m); setResults(null); };
  const [modelName, setModelName] = useState(() => localStorage.getItem('pm_modelName') || 'gemini-3.5-flash-lite');
  const modelOptions = Array.from(new Set([localStorage.getItem('pm_modelName') || 'gemini-3.5-flash-lite', 'gemini-3.6-flash', 'gemini-2.0-flash-lite']));
  const [imageMode, setImageMode] = useState('original');
  const [analyzeConcurrency, setAnalyzeConcurrency] = useState(DEFAULT_CONCURRENCY);

  // AI生成パラメータ（実験用・superadmin限定）
  const [thinkingLevel, setThinkingLevel] = useState('default');
  const [temperatureMode, setTemperatureMode] = useState('default');
  // 数値の temperature を受け付けないモデルに切り替えたら既定へ戻す（選べないはずの値のまま実行させない）
  useEffect(() => {
    if (!isTemperatureModeSupported(modelName, temperatureMode)) setTemperatureMode('default');
  }, [modelName, temperatureMode]);
  const [mediaResolution, setMediaResolution] = useState('default');
  // 誤操作防止のため既定で閉じておく（本番影響のある実験用パラメータのため）
  const [showExperimental, setShowExperimental] = useState(false);

  // 網羅的自動テスト（実験用・superadmin限定）: モデル×思考レベル×temperatureの全組み合わせを順次実行
  const [sweepModels, setSweepModels] = useState(() => new Set([modelName]));
  const [sweepThinkingLevels, setSweepThinkingLevels] = useState(() => new Set(['default']));
  const [sweepTemperatureModes, setSweepTemperatureModes] = useState(() => new Set(['default']));
  const [sweepRunning, setSweepRunning] = useState(false);
  const [sweepPattern, setSweepPattern] = useState(null);
  const [sweepResults, setSweepResults] = useState([]);
  const toggleSweepValue = (setFn, value) => setFn(prev => { const next = new Set(prev); next.has(value) ? next.delete(value) : next.add(value); return next; });
  const sweepComboCount = sweepModels.size * sweepThinkingLevels.size * sweepTemperatureModes.size;

  const [running, setRunning] = useState(false);
  const [aborting, setAborting] = useState(false);
  const abortRef = useRef(false);
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);
  const [resultsModel, setResultsModel] = useState(null);
  const [expandedRefs, setExpandedRefs] = useState(new Set());
  const [zoomImage, setZoomImage] = useState(null);
  const [timingLog, setTimingLog] = useState([]);
  const [showTimingLog, setShowTimingLog] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const busy = running || sweepRunning;

  const companyIds = useMemo(() => companies.map(c => c.id), [companies]);

  const fetchRefs = useCallback(async () => {
    setLoading(true);
    let query = supabase.from('pm_reference_labels')
      .select('*, inspections:pm_inspections(serial, store_name, dept_name, label_product_name, detected_food_visual, match_status, reason, image_urls, image_mode)')
      .order('created_at', { ascending: false });
    if (companyIds.length > 0) query = query.in('company_id', companyIds);
    if (filterCompany) query = query.eq('company_id', filterCompany);
    const { data } = await query;
    setRefs((data || []).sort((a, b) => (a.label || '').localeCompare(b.label || '', 'ja')));
    setLoading(false);
  }, [filterCompany, companyIds]);

  useEffect(() => { fetchRefs(); }, [fetchRefs]);

  const selectAll = () => setSelectedIds(prev => new Set([...prev, ...refs.map(r => r.id)]));
  const deselectAll = () => setSelectedIds(prev => { const next = new Set(prev); refs.forEach(r => next.delete(r.id)); return next; });
  const toggleSelect = (id) => setSelectedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });

  const deleteRef = async (id) => {
    if (!window.confirm('この基準ラベルを削除しますか？')) return;
    await supabase.from('pm_reference_labels').delete().eq('id', id);
    setRefs(prev => prev.filter(r => r.id !== id));
    setSelectedIds(prev => { const next = new Set(prev); next.delete(id); return next; });
  };

  const runTest = async () => {
    const targets = refs.filter(r => selectedIds.has(r.id) && r.inspections?.image_urls?.length > 0);
    if (targets.length === 0) { alert('テスト対象が選択されていません'); return; }
    setRunning(true); setAborting(false); abortRef.current = false; setResults(null);

    const tStart = performance.now();
    const elapsed = () => `${(performance.now() - tStart).toFixed(0)}ms`;
    const logs = [];
    const logTiming = (msg) => { console.log(`[regression-test] ${msg}`); logs.push(msg); };
    setTimingLog([]);
    logTiming(`開始 対象${targets.length}件`);

    const token = (await supabase.auth.getSession()).data?.session?.access_token;
    if (!token) { alert('認証エラー'); setRunning(false); return; }
    logTiming(`トークン取得完了 累計${elapsed()}`);

    const iterations = mode === 'stability' ? 10 : 1;
    const isAborted = () => abortRef.current;

    const tPhase1 = performance.now();
    const imagesByRef = await downloadImages(targets, { concurrency: DOWNLOAD_CONCURRENCY, isAborted });
    logTiming(`画像DL完了 所要${(performance.now() - tPhase1).toFixed(0)}ms 累計${elapsed()}`);

    const allResults = await runAnalyzeBatch(targets, imagesByRef, {
      token, model: modelName, thinkingLevel, temperatureMode, mediaResolution,
      iterations, mode, concurrency: analyzeConcurrency, isAborted, onProgress: setProgress,
    });

    logTiming(`全体完了 総所要${elapsed()}`);
    setTimingLog(logs);
    setResults(allResults); setResultsModel(modelName); setRunning(false); setProgress(null);

    // 履歴をDBに保存（失敗しても結果表示は止めない）
    try {
      await saveRunHistory(allResults, {
        mode, model: modelName, appVersion: `v${pkg.version}`, imageMode,
        thinkingLevel, temperatureMode, mediaResolution,
        duration: performance.now() - tStart,
        companyId: companies[0]?.id, createdBy: profile?.email,
      });
      setHistoryRefreshKey(k => k + 1);
    } catch (err) { console.error('[history-save]', err.message); }
  };

  // モデル×思考レベル×temperatureの全組み合わせを順に自動実行する（網羅テスト）。
  // 画像ダウンロードは全パターンで1回のみ共有し、画像解像度・検査モードは現在の単一選択値を使う。
  const runSweep = async () => {
    const targets = refs.filter(r => selectedIds.has(r.id) && r.inspections?.image_urls?.length > 0);
    if (targets.length === 0) { alert('テスト対象が選択されていません'); return; }

    if (!sweepModels.size || !sweepThinkingLevels.size || !sweepTemperatureModes.size) {
      alert('モデル・思考レベル・temperatureをそれぞれ1つ以上選択してください');
      return;
    }
    const models = [...sweepModels];
    const levels = [...sweepThinkingLevels];
    const temps = [...sweepTemperatureModes];
    const combos = [];
    // 受け付けない temperature の組み合わせ（例: gemini-3.6-flash × 0.3）は作らない（Gemini では効かないか 400）
    for (const model of models) for (const tl of levels) for (const tp of temps) {
      if (isTemperatureModeSupported(model, tp)) combos.push({ model, thinkingLevel: tl, temperatureMode: tp });
    }
    if (combos.length === 0) { alert('選んだモデルでは、選んだ temperature の組み合わせを実行できません'); return; }

    const totalCalls = combos.length * targets.length;
    if (!window.confirm(`${combos.length}パターン × ${targets.length}件 = 最大${totalCalls}回のAPI呼び出しを順次実行します。よろしいですか？`)) return;

    setSweepRunning(true); setAborting(false); abortRef.current = false;
    setSweepResults([]); setSweepPattern(null); setResults(null); setTimingLog([]);

    const token = (await supabase.auth.getSession()).data?.session?.access_token;
    if (!token) { alert('認証エラー'); setSweepRunning(false); return; }
    const isAborted = () => abortRef.current;

    const imagesByRef = await downloadImages(targets, { concurrency: DOWNLOAD_CONCURRENCY, isAborted });

    for (let i = 0; i < combos.length; i++) {
      if (abortRef.current) break;
      const combo = combos[i];
      setSweepPattern({ index: i + 1, total: combos.length, ...combo });

      const tStart = performance.now();
      const allResults = await runAnalyzeBatch(targets, imagesByRef, {
        token, ...combo, mediaResolution,
        iterations: 1, mode: 'regression', concurrency: analyzeConcurrency, isAborted, onProgress: setProgress,
      });
      const duration = performance.now() - tStart;
      setResults(allResults); setResultsModel(combo.model);

      let summary = null;
      try {
        summary = await saveRunHistory(allResults, {
          mode: 'regression', model: combo.model, appVersion: `v${pkg.version}`, imageMode,
          thinkingLevel: combo.thinkingLevel, temperatureMode: combo.temperatureMode, mediaResolution,
          duration, companyId: companies[0]?.id, createdBy: profile?.email,
        });
        setHistoryRefreshKey(k => k + 1);
      } catch (err) { console.error('[history-save]', err.message); }

      setSweepResults(prev => [...prev, {
        ...combo,
        labelCount: summary?.label_count ?? allResults.length,
        matchCount: summary?.match_count ?? allResults.filter(r => !r.error && r.match).length,
        medianMs: summary?.latency_median_ms ?? null,
      }]);
    }

    setSweepRunning(false); setSweepPattern(null); setProgress(null);
  };

  const handleAbort = () => { abortRef.current = true; setAborting(true); };

  const updateBaseline = async (r) => {
    const run = r.run;
    if (!run) return;
    if (!window.confirm('基準を現在の結果で更新しますか？')) return;
    const { error } = await supabase.from('pm_reference_labels').update({ expected_match_status: run.match_status }).eq('id', r.ref.id);
    if (error) { alert('更新失敗: ' + error.message); return; }
    setRefs(prev => prev.map(ref => ref.id === r.ref.id ? { ...ref, expected_match_status: run.match_status } : ref));
    setResults(prev => prev.map(p => p.ref.id === r.ref.id ? { ...p, expected: run.match_status, match: true } : p));
  };

  const applyAllBaselines = async () => {
    if (!results) return;
    const targets = results.filter(r => !r.error && !r.match);
    if (targets.length === 0) { alert('反映対象がありません'); return; }
    if (!window.confirm(`${targets.length}件の基準を現在の結果で更新します。よろしいですか？`)) return;
    for (const r of targets) {
      await supabase.from('pm_reference_labels').update({ expected_match_status: r.run.match_status }).eq('id', r.ref.id);
    }
    setRefs(prev => prev.map(ref => { const t = targets.find(r => r.ref.id === ref.id); return t ? { ...ref, expected_match_status: t.run.match_status } : ref; }));
    setResults(prev => prev.map(p => { const t = targets.find(r => r.ref.id === p.ref.id); return t ? { ...p, expected: t.run.match_status, match: true } : p; }));
    alert(`${targets.length}件の基準を更新しました`);
  };

  const downloadJSON = () => {
    if (!results) return;
    const json = results.map(r => ({ label: r.ref.label, model: resultsModel, expected: r.expected || null, actual: r.run?.match_status || null, match: r.match ?? null, reason: r.run?.reason || null, analyzeMs: r.run?.analyzeMs || null, error: r.error || null }));
    const blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `PM_test_results_${Date.now()}.json`; a.click(); URL.revokeObjectURL(url);
  };

  const exportResultsCSV = () => {
    if (!results) return;
    if (mode === 'regression') {
      const headers = ['ラベル名', '期待', '実際', '一致', '理由', 'エラー', '判定時間(秒)', 'モデル'];
      const rows = results.map(r => r.error ? [r.ref?.label, '', '', '', '', r.error, '', resultsModel] : [r.ref?.label, r.expected, r.actual, r.match ? 'OK' : 'NG', r.run?.reason || '', '', r.run?.analyzeMs != null ? (r.run.analyzeMs / 1000).toFixed(2) : '', resultsModel]);
      exportCSV(headers, rows, `PM_regression_${new Date().toISOString().slice(0, 10)}.csv`);
    } else {
      const headers = ['ラベル名', '安定度(%)', '多数派', '試行回数', 'エラー', 'モデル'];
      const rows = results.map(r => r.error ? [r.ref?.label, '', '', '', r.error, resultsModel] : [r.ref?.label, r.stability?.score, r.stability?.majority, r.validRuns, '', resultsModel]);
      exportCSV(headers, rows, `PM_stability_${new Date().toISOString().slice(0, 10)}.csv`);
    }
  };

  const passCount = results?.filter(r => mode === 'regression' ? r.match === true : r.stability?.score === 100).length || 0;
  const failCount = results ? results.length - passCount - (results.filter(r => r.error).length) : 0;
  const errorCount = results?.filter(r => r.error).length || 0;

  return (
    <div className="space-y-4">
      {/* 基準ラベル一覧 */}
      <div className="bg-white rounded-lg border">
        <div className="px-4 py-3 border-b flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs font-bold text-slate-500">
            基準ラベル一覧 ({refs.length}件)
            {(() => { const n = refs.filter(r => selectedIds.has(r.id)).length; return n > 0 ? <span className="ml-2 text-indigo-600">✓{n}件選択</span> : null; })()}
          </p>
          <div className="flex items-center gap-2">
            <button onClick={selectAll} className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-slate-200">全選択</button>
            <button onClick={deselectAll} className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-slate-200">全解除</button>
            {companies.length > 1 && (
              <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} className="text-xs border rounded px-2 py-1">
                {isSuperAdmin && <option value="">全企業</option>}
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
          </div>
        </div>
        <div className="divide-y max-h-64 overflow-y-auto">
          {loading ? <p className="text-sm text-slate-400 text-center py-4">読み込み中...</p> : refs.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">基準ラベルがありません</p> : refs.map(ref => {
            const insp = ref.inspections;
            const isSelected = selectedIds.has(ref.id);
            return (
              <div key={ref.id} className="px-4 py-2 flex items-center gap-2">
                <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(ref.id)} className="w-4 h-4 shrink-0 accent-indigo-600" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-700 truncate">{ref.label}</div>
                  <div className="text-[10px] text-slate-400">
                    {companies.find(c => c.id === ref.company_id)?.name || ''} · {formatSerial(insp?.serial, 'PM-?')} · {insp?.label_product_name || ''} · <span className={(ref.expected_match_status || insp?.match_status) === 'DANGER' ? 'text-red-600' : 'text-blue-600'}>{ref.expected_match_status || insp?.match_status}</span>
                  </div>
                </div>
                {insp?.image_urls?.length > 0 && (
                  <span className="text-[10px] text-slate-400 shrink-0">{insp.image_urls.length}枚</span>
                )}
                <button onClick={() => deleteRef(ref.id)} className="text-slate-300 hover:text-red-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            );
          })}
        </div>
      </div>

      {/* テスト実行パネル */}
      <div className="bg-white rounded-lg border p-4 space-y-3">
        <p className="text-xs font-bold text-slate-500">テスト実行</p>
        <div className="flex gap-2">
          <button onClick={() => changeMode('regression')} disabled={busy} className={`flex-1 py-2 text-xs font-bold rounded ${mode === 'regression' ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-500'}`}>リグレッション (1回)</button>
          <button onClick={() => changeMode('stability')} disabled={busy} className={`flex-1 py-2 text-xs font-bold rounded ${mode === 'stability' ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-500'}`}>安定性 (10回)</button>
        </div>
        <div>
          <label className="text-[10px] text-slate-400">モデル</label>
          <select value={modelName} onChange={e => setModelName(e.target.value)} disabled={busy} className="w-full text-sm border rounded px-2 py-1.5">{modelOptions.map(m => <option key={m} value={m}>{m}</option>)}</select>
        </div>
        <div>
          <label className="text-[10px] text-slate-400">検査モード</label>
          <select value={imageMode} onChange={e => setImageMode(e.target.value)} disabled={busy} className="w-full text-sm border rounded px-2 py-1.5">
            <option value="original">初回検査時のモード（既定）</option>
            <option value="speed">速度優先（一律）</option>
            <option value="accuracy">精度優先（一律）</option>
          </select>
          {imageMode === 'original' && <p className="text-[10px] text-slate-400 mt-0.5">各基準ラベルの登録時の検査モードを個別に使用します（未記録は速度優先）</p>}
        </div>
        <div>
          <label className="text-[10px] text-slate-400">同時実行数（Gemini呼び出し）</label>
          <input type="number" min={1} max={50} value={analyzeConcurrency} onChange={e => setAnalyzeConcurrency(Math.max(1, Math.min(50, parseInt(e.target.value) || DEFAULT_CONCURRENCY)))} disabled={busy} className="w-full text-sm border rounded px-2 py-1.5" />
          <p className="text-[10px] text-slate-400 mt-0.5">429の発生率を見ながら調整してください（デフォルト{DEFAULT_CONCURRENCY}）。初回投入は{STAGGER_MS}msずつずらして起動します</p>
        </div>

        {isSuperAdmin && (
        <div className="border rounded-lg overflow-hidden">
          <button type="button" onClick={() => setShowExperimental(v => !v)} className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100">
            <span className="text-[10px] font-bold text-slate-500">AI生成パラメータ（実験用） — 本番挙動に影響するため通常は操作不要</span>
            {showExperimental ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
          </button>
          {showExperimental && (
          <div className="p-3 pt-2">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] text-slate-400">思考レベル</label>
              <select value={thinkingLevel} onChange={e => setThinkingLevel(e.target.value)} disabled={busy} className={`w-full text-xs border rounded px-1.5 py-1 ${thinkingLevel !== 'default' ? 'border-purple-400 bg-purple-50 text-purple-800 font-bold' : ''}`}>
                {THINKING_LEVEL_OPTIONS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-400">temperature</label>
              <select value={temperatureMode} onChange={e => setTemperatureMode(e.target.value)} disabled={busy} className={`w-full text-xs border rounded px-1.5 py-1 ${temperatureMode !== 'default' ? 'border-purple-400 bg-purple-50 text-purple-800 font-bold' : ''}`}>
                {TEMPERATURE_OPTIONS.map(({ value, label }) => {
                  const unsupported = !isTemperatureModeSupported(modelName, value);
                  return <option key={value} value={value} disabled={unsupported}>{label}{unsupported ? '（このモデルは非対応）' : ''}</option>;
                })}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-400">画像解像度</label>
              <select value={mediaResolution} onChange={e => setMediaResolution(e.target.value)} disabled={busy} className={`w-full text-xs border rounded px-1.5 py-1 ${mediaResolution !== 'default' ? 'border-purple-400 bg-purple-50 text-purple-800 font-bold' : ''}`}>
                <option value="default">現状（モデル既定）</option>
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
              </select>
            </div>
          </div>

          {/* 網羅的自動テスト */}
          <div className="border-t mt-3 pt-3">
            <p className="text-[10px] font-bold text-slate-500 mb-1">網羅的自動テスト（実験用）</p>
            <p className="text-[10px] text-slate-400 mb-2">チェックした値の全組み合わせを順番に自動実行し、履歴に保存します（画像ダウンロードは1回のみ共有・画像解像度は上の設定を使用）。</p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] text-slate-400">モデル</label>
                <div className="space-y-0.5 mt-0.5">
                  {modelOptions.map(m => (
                    <label key={m} className="flex items-center gap-1.5 text-[11px] text-slate-600">
                      <input type="checkbox" checked={sweepModels.has(m)} disabled={busy} onChange={() => toggleSweepValue(setSweepModels, m)} className="w-3.5 h-3.5 accent-purple-600" />
                      {m}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] text-slate-400">思考レベル</label>
                <div className="space-y-0.5 mt-0.5">
                  {THINKING_LEVEL_OPTIONS.map(({ value, label }) => (
                    <label key={value} className="flex items-center gap-1.5 text-[11px] text-slate-600">
                      <input type="checkbox" checked={sweepThinkingLevels.has(value)} disabled={busy} onChange={() => toggleSweepValue(setSweepThinkingLevels, value)} className="w-3.5 h-3.5 accent-purple-600" />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] text-slate-400">temperature</label>
                <div className="space-y-0.5 mt-0.5">
                  {TEMPERATURE_OPTIONS.map(({ value, label }) => (
                    <label key={value} className="flex items-center gap-1.5 text-[11px] text-slate-600">
                      <input type="checkbox" checked={sweepTemperatureModes.has(value)} disabled={busy} onChange={() => toggleSweepValue(setSweepTemperatureModes, value)} className="w-3.5 h-3.5 accent-purple-600" />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {!sweepRunning ? (
              <button onClick={runSweep} disabled={running || selectedIds.size === 0 || sweepComboCount === 0} className="w-full mt-2 py-2 bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold rounded disabled:bg-slate-300 flex items-center justify-center gap-2">
                <Play className="w-3.5 h-3.5" /> 網羅テストを開始（{sweepComboCount}パターン）
              </button>
            ) : (
              <div className="mt-2 space-y-2">
                <div className="flex items-center justify-between text-[11px] text-slate-500">
                  <span>パターン {sweepPattern?.index}/{sweepPattern?.total}: <span className="font-mono">{sweepPattern?.model}</span> / 思考:{sweepPattern?.thinkingLevel} / temp:{sweepPattern?.temperatureMode}</span>
                  {progress && <span>{progress.current}/{progress.total}件</span>}
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2"><div className="bg-purple-500 h-2 rounded-full transition-all" style={{ width: `${progress && progress.total ? (progress.current / progress.total) * 100 : 0}%` }} /></div>
                <button onClick={handleAbort} disabled={aborting} className="w-full py-1.5 bg-red-500 hover:bg-red-400 text-white text-xs font-bold rounded flex items-center justify-center gap-1">
                  <Square className="w-3 h-3" /> {aborting ? '中止中...' : '中止'}
                </button>
              </div>
            )}

            {sweepResults.length > 0 && (
              <div className="mt-3 border rounded overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-slate-400 text-left border-b">
                      <th className="px-2 py-1 font-normal">モデル</th>
                      <th className="px-2 py-1 font-normal">思考</th>
                      <th className="px-2 py-1 font-normal">temp</th>
                      <th className="px-2 py-1 font-normal text-right">正答率</th>
                      <th className="px-2 py-1 font-normal text-right">中央値検査時間</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {sweepResults.map((r, i) => (
                      <tr key={i}>
                        <td className="px-2 py-1 font-mono text-slate-600">{r.model}</td>
                        <td className="px-2 py-1 text-slate-500">{r.thinkingLevel === 'default' ? '—' : r.thinkingLevel}</td>
                        <td className="px-2 py-1 text-slate-500">{r.temperatureMode === 'default' ? '—' : r.temperatureMode}</td>
                        <td className="px-2 py-1 text-right font-bold text-slate-700">{r.labelCount ? `${((r.matchCount / r.labelCount) * 100).toFixed(1)}%` : '—'}</td>
                        <td className="px-2 py-1 text-right text-slate-500">{r.medianMs != null ? `${(r.medianMs / 1000).toFixed(2)}s` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </div>
          )}
        </div>
        )}

        {!busy && !results && (
          <button onClick={runTest} disabled={selectedIds.size === 0} className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold rounded disabled:bg-slate-300 flex items-center justify-center gap-2">
            <Play className="w-4 h-4" /> テスト開始 ({refs.filter(r => selectedIds.has(r.id) && r.inspections?.image_urls?.length > 0).length}件)
          </button>
        )}
        {running && progress && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>実行中...</span><span>{progress.current}/{progress.total}</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2"><div className="bg-indigo-500 h-2 rounded-full transition-all" style={{ width: `${(progress.current / progress.total) * 100}%` }} /></div>
            <button onClick={handleAbort} disabled={aborting} className="w-full py-2 bg-red-500 hover:bg-red-400 text-white text-xs font-bold rounded flex items-center justify-center gap-1">
              <Square className="w-3 h-3" /> {aborting ? '中止中...' : '中止'}
            </button>
          </div>
        )}

        {timingLog.length > 0 && (
          <div className="mt-2 border rounded-lg overflow-hidden">
            <button onClick={() => setShowTimingLog(v => !v)} className="w-full px-2 py-1.5 text-[11px] text-slate-500 hover:bg-slate-50 flex items-center justify-between">
              <span>⏱ タイミングログ（{timingLog.length}件）— 遅さ調査用</span>
              <span>{showTimingLog ? '閉じる' : '表示'}</span>
            </button>
            {showTimingLog && (
              <div className="px-2 py-1.5 bg-slate-900 text-slate-100 text-[10px] font-mono max-h-48 overflow-y-auto whitespace-pre-wrap">
                {timingLog.join('\n')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 結果 */}
      {results && (
        <div className="bg-white rounded-lg border">
          <div className="px-4 py-3 border-b flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <p className="text-xs font-bold text-slate-500">{mode === 'regression' ? 'リグレッション結果' : '安定性テスト結果'}</p>
              <span className="text-xs font-bold text-green-600">{passCount} PASS</span>
              {failCount > 0 && <span className="text-xs font-bold text-red-600">{failCount} FAIL</span>}
              {errorCount > 0 && <span className="text-xs text-yellow-600">ERR {errorCount}</span>}
            </div>
            <div className="flex gap-2 flex-wrap">
              {mode === 'regression' && failCount > 0 && (
                <button onClick={applyAllBaselines} className="text-xs px-2 py-1 bg-orange-50 text-orange-700 rounded hover:bg-orange-100 font-bold">全件反映 ({failCount}件)</button>
              )}
              <button disabled={pdfBusy} onClick={() => runPdf(() => openBatchPdf(results, resultsModel, profile?.email, { onIOSPdfReady: shareOrDefer }))} className="text-xs px-2 py-1 bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 disabled:opacity-50 flex items-center gap-1">{pdfBusy ? '生成中...' : '全件PDF'}</button>
              {/* iOSで共有シートを開けなかった場合だけ表示。このボタンのタップを新しい
                  一時的な許可として共有し直す（usePendingPdfShare参照） */}
              {pendingPdf && (
                <button onClick={sharePendingPdf} className="text-xs px-2 py-1 bg-amber-500 text-white rounded hover:bg-amber-400 font-bold flex items-center gap-1">PDFを保存</button>
              )}
              <button onClick={downloadJSON} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 flex items-center gap-1"><Download className="w-3 h-3" /> JSON</button>
              <button onClick={exportResultsCSV} className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100 flex items-center gap-1"><Download className="w-3 h-3" /> CSV</button>
              <button onClick={() => { setResults(null); setExpandedRefs(new Set()); }} className="text-xs px-2 py-1 bg-slate-50 text-slate-500 rounded hover:bg-slate-100">クリア</button>
            </div>
          </div>
          <div className="divide-y max-h-[600px] overflow-y-auto">
            {results.map((r, idx) => {
              const isPass = !r.error && (mode === 'regression' ? r.match : r.stability?.score === 100);
              const isExpanded = expandedRefs.has(idx);
              return (
                <div key={idx}>
                  <button className="w-full text-left px-4 py-2 flex items-center gap-2 hover:bg-slate-50" onClick={() => setExpandedRefs(prev => { const next = new Set(prev); next.has(idx) ? next.delete(idx) : next.add(idx); return next; })}>
                    {r.error ? <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0" /> : isPass ? <CheckCircle className="w-4 h-4 text-green-500 shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
                    <span className="text-sm flex-1 truncate">{r.ref.label}</span>
                    {mode === 'regression' && !r.error && isBlurryImage(r.run?.image_quality) && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white">{BLURRY_IMAGE_LABEL}</span>}
                    {mode === 'regression' && !r.error && !r.match && <span className="text-[10px] text-red-500">{r.expected}→{r.actual}</span>}
                    {mode === 'stability' && !r.error && <span className={`text-[10px] font-bold ${r.stability.score === 100 ? 'text-green-600' : 'text-red-600'}`}>{r.stability.score}%</span>}
                    {isExpanded ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-3 bg-slate-50 text-xs space-y-2">
                      {r.ref.inspections?.image_urls?.length > 0 && (
                        <div className="flex gap-2 mb-2">
                          {r.ref.inspections.image_urls.map((path, i) => (
                            <StorageImage key={i} path={path} className="h-20 w-20 object-cover rounded border cursor-pointer" alt={`img-${i}`} onClick={() => setZoomImage(path)} />
                          ))}
                        </div>
                      )}
                      {r.error && <p className="text-red-500">エラー: {r.error}</p>}
                      {mode === 'regression' && !r.error && (
                        <>
                          <p className="text-slate-600">判定: {r.expected} → {r.actual} {r.match ? <span className="text-green-600">✓</span> : <span className="text-red-600">✗</span>}</p>
                          {r.run?.reason && <p className="text-slate-500">理由: {r.run.reason}</p>}
                          {r.run?.detected_food_visual && <p className="text-slate-500">目視: {r.run.detected_food_visual}</p>}
                          {r.run?.quantity_status && r.run.quantity_status !== 'N/A' && <p className="text-slate-500">数量: {r.run.quantity_status}</p>}
                          {r.run?.label_consistency && r.run.label_consistency !== 'N/A' && <p className="text-slate-500">ラベル整合: {r.run.label_consistency}</p>}
                          {r.run?.analyzeMs != null && <p className="text-slate-400">⏱ {(r.run.analyzeMs / 1000).toFixed(2)}s</p>}
                          {!r.match && (
                            <button onClick={() => updateBaseline(r)} className="text-[10px] px-2 py-0.5 rounded bg-orange-50 text-orange-700 font-bold hover:bg-orange-100 mt-1">
                              基準を現在の結果で更新
                            </button>
                          )}
                        </>
                      )}
                      {mode === 'stability' && !r.error && (
                        <>
                          <p className="text-slate-500">有効: {r.validRuns}/10 · 安定度: {r.stability.score}%</p>
                          <p className="text-slate-500">内訳: {Object.entries(r.stability.counts).map(([k, v]) => `${k}: ${v}回`).join(' / ')}</p>
                        </>
                      )}
                      {!r.error && r.run && (
                        <div className="flex gap-2 mt-1">
                          <button className="text-[10px] px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100" disabled={pdfBusy} onClick={() => runPdf(() => openTestPdf(r, profile?.email, { onIOSPdfReady: shareOrDefer }))}>PDF</button>
                          <button className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-700 hover:bg-slate-200" onClick={() => navigator.clipboard.writeText(JSON.stringify(r.run, null, 2))}>JSON コピー</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 実行履歴 */}
      {companies[0]?.id && (
        <RegressionHistory companyId={companies[0].id} refreshKey={historyRefreshKey} />
      )}

      {/* 画像ズーム */}
      <ImageZoomOverlay path={zoomImage} onClose={() => setZoomImage(null)} />
    </div>
  );
}
